/**
 * lib/studentDocuments.ts
 * ------------------------------------------------------------------
 * CRUD helpers for the per-student documents feature (Round 3).
 *
 * Layout:
 *   Firebase Storage:    studentDocuments/{studentId}/{docId}{ext}
 *   Firestore:           reportStudentDocuments/{docId}
 *
 * The Firestore doc is the source of truth for metadata (type,
 * uploader, size, etc.); the Storage file holds the bytes. The two
 * are written together on upload and deleted together on remove.
 *
 * Access control is enforced by the rules; this file just provides
 * the client API. If a caller doesn't have permission, the storage /
 * firestore SDK throws and the UI surfaces the error.
 *
 * Allowed file types: PDF + images (JPEG, PNG, WebP).
 * Max size: 10 MB.
 * ------------------------------------------------------------------
 */
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import type { StudentDocument, StudentDocumentType } from '@/types';

const COLLECTION = 'reportStudentDocuments';
const STORAGE_ROOT = 'studentDocuments';

export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
export const ALLOWED_CONTENT_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
]);
export const ALLOWED_EXTENSIONS = new Set(['pdf', 'jpg', 'jpeg', 'png', 'webp']);

/* ---- helpers ---- */

function extensionFor(contentType: string, filename: string): string {
  // Trust content-type first; fall back to filename extension
  if (contentType === 'application/pdf') return 'pdf';
  if (contentType === 'image/jpeg') return 'jpg';
  if (contentType === 'image/png') return 'png';
  if (contentType === 'image/webp') return 'webp';
  const m = filename.toLowerCase().match(/\.([a-z0-9]+)$/);
  return m ? m[1] : 'bin';
}

/**
 * Validate a file BEFORE attempting upload. Returns an error message
 * for invalid files, or null if the file passes.
 */
export function validateDocumentFile(file: File): string | null {
  if (file.size === 0) return 'File is empty.';
  if (file.size > MAX_FILE_SIZE_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    return `File is ${mb} MB — maximum is 10 MB.`;
  }
  if (!ALLOWED_CONTENT_TYPES.has(file.type)) {
    // Fall back to extension check (some browsers send empty MIME)
    const m = file.name.toLowerCase().match(/\.([a-z0-9]+)$/);
    const ext = m ? m[1] : '';
    if (!ALLOWED_EXTENSIONS.has(ext)) {
      return 'Only PDF and image files (JPG, PNG, WebP) are accepted.';
    }
  }
  return null;
}

/* ---- API ---- */

export interface UploadArgs {
  studentId: string;
  file: File;
  type: StudentDocumentType;
  customLabel?: string;
  notes?: string;
  uploadedBy: string;
  uploadedByName?: string;
}

/**
 * Upload a new document for a student. Writes the storage file first
 * (so we don't end up with a metadata doc pointing to nothing), then
 * the Firestore doc. If the Firestore write fails, we attempt to
 * roll back the storage upload — but if that rollback itself fails,
 * the orphan is harmless (just unreferenced bytes).
 */
export async function uploadStudentDocument(args: UploadArgs): Promise<StudentDocument> {
  const validation = validateDocumentFile(args.file);
  if (validation) throw new Error(validation);

  const docId = `sdoc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const ext = extensionFor(args.file.type || 'application/octet-stream', args.file.name);
  const storagePath = `${STORAGE_ROOT}/${args.studentId}/${docId}.${ext}`;

  const storageRef = ref(storage, storagePath);
  const contentType = ALLOWED_CONTENT_TYPES.has(args.file.type)
    ? args.file.type
    : 'application/octet-stream';

  // 1. Upload bytes
  await uploadBytes(storageRef, args.file, { contentType });

  // 2. Write Firestore doc
  const now = new Date().toISOString();
  const record: StudentDocument = {
    id: docId,
    studentId: args.studentId,
    type: args.type,
    filename: args.file.name,
    contentType,
    size: args.file.size,
    storagePath,
    uploadedAt: now,
    uploadedBy: args.uploadedBy,
    ...(args.customLabel ? { customLabel: args.customLabel } : {}),
    ...(args.notes ? { notes: args.notes } : {}),
    ...(args.uploadedByName ? { uploadedByName: args.uploadedByName } : {}),
  };

  try {
    await setDoc(doc(db, COLLECTION, docId), record);
  } catch (e) {
    // Roll back storage on metadata failure
    try {
      await deleteObject(storageRef);
    } catch {
      // Best effort — leaves an unreferenced blob; not fatal.
    }
    throw e;
  }

  return record;
}

/**
 * List documents for one student, newest first. Caller MUST be
 * authorised to read (admissions staff or linked parent); the
 * Firestore rules enforce this.
 */
export async function listStudentDocuments(studentId: string): Promise<StudentDocument[]> {
  const q = query(
    collection(db, COLLECTION),
    where('studentId', '==', studentId),
    orderBy('uploadedAt', 'desc'),
  );
  const snap = await getDocs(q);
  const out: StudentDocument[] = [];
  snap.forEach((d) => out.push(d.data() as StudentDocument));
  return out;
}

/**
 * Get a signed download URL for a document. URLs are time-limited but
 * generally last long enough for the user to click; if you store the
 * URL, fetch a fresh one each time.
 */
export async function getDocumentDownloadUrl(record: StudentDocument): Promise<string> {
  const r = ref(storage, record.storagePath);
  return await getDownloadURL(r);
}

/**
 * Delete a document. Removes the Firestore doc first (the source of
 * truth for "is this document visible"), then the storage file. If
 * the storage delete fails (e.g. already gone), we proceed — the
 * record is already orphaned out of view.
 */
export async function deleteStudentDocument(record: StudentDocument): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, record.id));
  try {
    await deleteObject(ref(storage, record.storagePath));
  } catch {
    // Best effort
  }
}

/* ---- display helpers ---- */

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function displayLabel(record: Pick<StudentDocument, 'type' | 'customLabel'>): string {
  if (record.type === 'other') return record.customLabel || 'Other document';
  // Lazy lookup to avoid an import cycle with types
  const map: Record<string, string> = {
    birth_certificate: 'Birth Certificate',
    passport: 'Passport',
    residence_permit: 'Residence Permit',
    parent_id: "Parent's ID",
    previous_transcript: 'Previous School Transcript',
    vaccination_record: 'Vaccination Record',
  };
  return map[record.type] ?? 'Document';
}
